import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import { ErrorAnalytics } from '../../lib/analytics';

interface ErrorBoundaryProps {
    children: ReactNode;
    componentName?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(_: Error): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({ error, errorInfo });
        
        // Track error with analytics
        ErrorAnalytics.appError(
            error.name || 'UnknownError',
            this.props.componentName || 'ErrorBoundary',
            error.message
        );

        console.error("Uncaught error:", error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
                    <Alert severity="error">
                        <AlertTitle>Something went wrong</AlertTitle>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
                        </Typography>
                        
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {this.state.error.message}
                                    {this.state.errorInfo && '\n\n' + this.state.errorInfo.componentStack}
                                </Typography>
                            </Box>
                        )}
                        
                        <Box sx={{ mt: 2 }}>
                            <Button 
                                variant="outlined" 
                                onClick={this.handleReset}
                                sx={{ mr: 1 }}
                            >
                                Try Again
                            </Button>
                            <Button 
                                variant="outlined" 
                                onClick={() => window.location.reload()}
                            >
                                Refresh Page
                            </Button>
                        </Box>
                    </Alert>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;